import { useState, useEffect, ChangeEvent } from 'react';
import { Card, Col, Row, Toast, ToastContainer } from 'react-bootstrap';

import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'components/base/DatePicker';
import {
    faFileExport,
    faFilter,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';


export interface Certificate {
    certificate_id: number;

    certificate_title: string;
    certificate_from: string;
    certificate_date: string;
    notes: string;
    inserted_at: string;
}

const CertificatesTable = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};


const Certificates = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },

        {
            label: t('Feedback'),
            url: '/feedback-dashboard'
        },
        {
            label: t('Certificates.page_title'),
            active: true
        }
    ];


    const auth = useAuth();
    const navigate = useNavigate();
    const handleClick = () => {
        // relative path
        navigate('add');
    };

    const [loading, setLoading] = useState(true);

    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    const [certificates, setCertificates] = useState<Certificate[]>([]);

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const certificateListTableColumns: ColumnDef<Certificate>[] = [
        {
            accessorKey: t('Certificate.table.title'),
            header: 'Certificate Title',
            cell: ({ row: { original } }) => {
                const { certificate_id, certificate_title } = original;
                return (
                    <Link to={`/feedback-dashboard/certificates/edit/${certificate_id}`} className="fw-semi-bold">
                        {certificate_title}
                    </Link>
                );
            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '25%' } }
            }
        },


        {
            header: t('Certificate.table.from'),
            accessorKey: 'certificate_from',
            meta: {
                headerProps: { style: { width: '10%' }, className: 'ps-3' }
            }
        },

        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '12%' }, className: 'ps-3' }
            }
        },


    ];

    const table = useAdvanceTable({
        data: certificates,
        columns: certificateListTableColumns,
        pageSize: 6,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/certificates',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            setCertificates(response.data);
        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <>

            <PageBreadcrumb items={breadcrumbItems} />


            <ToastContainer className="p-3" position='top-center'>
                <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
                    <Toast.Body className=''>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>

            <Card className='mt-3  mb-3 border border-light rounded-5' >
                <Card.Body>
                    <AdvanceTableProvider {...table}>

                        <Row className="g-3 justify-content-between mb-4">
                            <Col xs="auto">
                                <Button
                                    variant="primary"
                                    className="me-4"
                                    startIcon={
                                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                    }
                                    onClick={handleClick}
                                >
                                    {t('Certificates.add_certificate')}
                                </Button>

                                <Button
                                    variant="link"
                                    className="text-900 px-0"
                                    startIcon={
                                        <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                                    }
                                >
                                    {t("Certificates.export")}
                                </Button>
                            </Col>
                            <Col xs="auto">
                                <div className="d-flex">
                                    <SearchBox
                                        placeholder="Search by name"
                                        className="me-2"
                                        onChange={handleSearchInputChange}
                                    />
                                    <DatePicker defaultValue="Mar 1, 2022" />
                                    <Button
                                        variant="phoenix-secondary"
                                        className="px-3 ms-2"
                                        onClick={() => setOpenFilterModal(true)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFilter}
                                            transform="down-3"
                                            className="text-primary"
                                        />
                                    </Button>
                                </div>
                                <LeadsFilterModal
                                    show={openFilterModal}
                                    handleClose={() => setOpenFilterModal(false)}
                                />
                            </Col>
                        </Row>
                        <CertificatesTable />
                    </AdvanceTableProvider>
                </Card.Body>
            </Card>
        </>
    );


};

export default Certificates;
